// ADR-0008 / ADR-0001: role → job table. Shared so api/ enforces it and web/ shows the menu from the same source (#71).
import type { Role } from "./api";
import type { UserDenyReason } from "./audit";

export const PERMISSIONS = {
  view: ["super_admin", "admin", "operator", "viewer"],
  write: ["super_admin", "admin", "operator"], // upload, profile, justification, reconciliation, note
  report: ["super_admin", "admin", "operator", "viewer"], // PDF / xlsx, even though GET
  audit: ["super_admin", "admin"], // E8
  users: ["super_admin", "admin"], // E9 (ADR-0017); which role may touch which is checkUserCommand
  settings: ["super_admin", "admin"], // E10, read only (ADR-0017)
} as const satisfies Record<string, readonly Role[]>;

export type Job = keyof typeof PERMISSIONS;

export const ROLE_LABEL: Record<Role, string> = { super_admin: "Super Admin", admin: "Yönetici", operator: "Operatör", viewer: "Salt okur" };

// ---- User-command rules (ADR-0008 K11, ADR-0017). Pure: api/ enforces them, web/ shows buttons and role options from the same functions. ----

export type UserAction = "create" | "set-role" | "deactivate" | "activate" | "reset-password";

const MANAGED_BY_ADMIN: readonly Role[] = ["operator", "viewer"];

/**
 * Who may run which user command (spec §13, K11). `targetRole` is the target's current role (absent for create),
 * `newRole` the role being given (create, set-role). Returns the reason code when denied (glossary §8).
 */
export function checkUserCommand(i: { actor: Role; action: UserAction; targetRole?: Role; newRole?: Role }): UserDenyReason | null {
  if (i.actor === "super_admin") return null;
  if (i.actor !== "admin") return "ACTOR_ROLE_INSUFFICIENT";
  if (i.targetRole && !MANAGED_BY_ADMIN.includes(i.targetRole)) return "TARGET_ROLE_NOT_MANAGEABLE";
  if (i.newRole && !MANAGED_BY_ADMIN.includes(i.newRole)) return "ROLE_NOT_GRANTABLE";
  return null;
}

/** ADR-0017: on the API (not the CLI) a user may not target their own account. Compare usernames in normalized form. */
export function checkSelfTarget(actorUsername: string, targetUsername: string): UserDenyReason | null {
  return actorUsername === targetUsername ? "SELF_TARGET" : null;
}

/** The last active super_admin may be neither deactivated nor demoted. */
export function checkLastSuperAdmin(i: {
  action: UserAction; target: { role: Role; active: boolean }; newRole?: Role; activeSuperAdmins: number;
}): UserDenyReason | null {
  const removes = i.action === "deactivate" || (i.action === "set-role" && i.newRole !== "super_admin");
  if (removes && i.target.role === "super_admin" && i.target.active && i.activeSuperAdmins <= 1) {
    return "LAST_SUPER_ADMIN";
  }
  return null;
}
